import { useState, type FormEvent, type ReactNode } from 'react';
import { BookOpen, Download, ShieldCheck, UserRoundX } from 'lucide-react';
import {
  PORTAL_TERMS_TEXT,
  PORTAL_TERMS_TITLE,
  PORTAL_TERMS_VERSION,
  type ManualEditionDto,
  type StoreAccessDto,
} from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field } from './ui';

/**
 * O bloqueio dos aceites (PLAN §9, fatia m).
 *
 * Fica **dentro** do `AppShell`, e não no lugar dele: o cabeçalho continua na
 * tela com o seletor de loja e o botão de sair. Quem tem duas empresas precisa
 * poder trocar de loja durante o bloqueio, e ninguém pode ficar preso numa tela
 * sem saída.
 *
 * ## A ordem importa
 *
 * Primeiro o Termo de Uso, depois o Manual. O termo é pessoal e a pessoa resolve
 * sozinha; o Manual pode depender de outra pessoa da empresa. Pedir o segundo
 * antes do primeiro deixaria alguém parado numa tela que ele não tem como
 * destravar, sem nem ter feito a parte dele.
 *
 * ## O bloqueio é o mesmo do servidor
 *
 * A tela bloqueia enquanto **qualquer** loja do usuário estiver com a edição
 * vigente pendente — exatamente o critério do `acceptanceGuard`. Bloquear só a
 * loja corrente deixaria a tela liberada e o servidor respondendo 403 em tudo,
 * que é o pior dos dois mundos.
 */
export function AcceptanceGate({ children }: { children: ReactNode }) {
  const { user, refresh } = useAuth();

  if (!user) return <>{children}</>;

  if (!user.termsAccepted) return <TermsScreen onDone={refresh} />;

  const pendente = user.stores.find((loja) => !loja.manualAccepted);
  if (pendente && user.manualEdition) {
    return <ManualScreen store={pendente} edition={user.manualEdition} onDone={refresh} />;
  }

  return <>{children}</>;
}

function Moldura({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <Card>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 shrink-0 text-brand-600">{icon}</span>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
            <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
          </div>
        </div>
        <div className="mt-5 space-y-4">{children}</div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Termo de Uso — pessoal
// ---------------------------------------------------------------------------

/**
 * O texto vem da constante compartilhada, e não de uma requisição.
 *
 * É o mesmo módulo que o servidor usa para calcular o SHA-256 gravado no
 * aceite — então o que a pessoa lê é, por construção, o que fica registrado.
 * Buscar o texto por HTTP abriria a chance de exibir uma coisa e assinar outra.
 */
function TermsScreen({ onDone }: { onDone: () => Promise<void> }) {
  const [marcado, setMarcado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aceitar() {
    setSalvando(true);
    setErro(null);
    try {
      await api.post('/acceptance/terms', { version: PORTAL_TERMS_VERSION });
      await onDone();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível registrar o aceite.');
      setSalvando(false);
    }
  }

  return (
    <Moldura
      icon={<ShieldCheck className="size-6" aria-hidden />}
      title={PORTAL_TERMS_TITLE}
      subtitle={`Versão ${PORTAL_TERMS_VERSION}. Leia e aceite para usar o portal.`}
    >
      {erro && <Alert>{erro}</Alert>}

      {/* Rolagem própria, com o texto inteiro dentro: um `<details>` ou um
          "leia aqui" externo deixaria aceitar sem o texto ter estado na tela. */}
      <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/60 p-4">
        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-700">
          {PORTAL_TERMS_TEXT}
        </pre>
      </div>

      <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={marcado}
          onChange={(e) => setMarcado(e.target.checked)}
          className="mt-0.5 size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        <span>
          Li e aceito o {PORTAL_TERMS_TITLE} na versão {PORTAL_TERMS_VERSION}.
        </span>
      </label>

      <div className="flex justify-end">
        <Button disabled={!marcado} loading={salvando} onClick={() => void aceitar()}>
          Aceitar e continuar
        </Button>
      </div>

      <p className="text-xs text-slate-500">
        O aceite fica registrado com a sua identificação, data, hora e endereço IP.
      </p>
    </Moldura>
  );
}

// ---------------------------------------------------------------------------
// Manual do Fornecedor — da empresa, pelo responsável
// ---------------------------------------------------------------------------

function ManualScreen({
  store,
  edition,
  onDone,
}: {
  store: StoreAccessDto;
  edition: ManualEditionDto;
  onDone: () => Promise<void>;
}) {
  const nomeEdicao = edition.reference ? `${edition.title} (${edition.reference})` : edition.title;
  // No meio da frase, "a 20ª edição" lê melhor que o título inteiro repetido.
  const curta = edition.reference ?? edition.title;

  return (
    <Moldura
      icon={<BookOpen className="size-6" aria-hidden />}
      title="Aceite do Manual do Fornecedor"
      subtitle={`${store.companyName} · ${store.protheusRef}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800">{nomeEdicao}</p>
          <p className="text-xs text-slate-500">
            É esta edição que precisa ser aceita. Leia antes de confirmar.
          </p>
        </div>
        <a
          href={`/api/files/${edition.fileId}`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
        >
          <Download className="size-4" aria-hidden />
          Baixar o Manual
        </a>
      </div>

      {store.isResponsible ? (
        <FormularioDeAceite store={store} edition={edition} onDone={onDone} />
      ) : (
        <AguardandoResponsavel store={store} edicao={curta} />
      )}
    </Moldura>
  );
}

/**
 * O formulário do responsável.
 *
 * Reproduz o `RQ 271-00`: a empresa confirma o recebimento e o aceite dos
 * requisitos do Manual na edição vigente. O único campo digitado é o **cargo** —
 * empresa e CNPJ vêm do cadastro, e assinatura e carimbo são substituídos pela
 * sessão autenticada e pela trilha de auditoria.
 */
function FormularioDeAceite({
  store,
  edition,
  onDone,
}: {
  store: StoreAccessDto;
  edition: ManualEditionDto;
  onDone: () => Promise<void>;
}) {
  const { user } = useAuth();
  const [cargo, setCargo] = useState('');
  const [marcado, setMarcado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});

  async function aceitar(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);
    setErro(null);
    setCampos({});
    try {
      await api.post(`/stores/${store.storeId}/manual-acceptance`, {
        documentId: edition.documentId,
        responsibleRole: cargo,
      });
      await onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setErro(err.fields ? null : err.message);
        setCampos(err.fields ?? {});
      } else {
        setErro('Não foi possível registrar o aceite.');
      }
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={aceitar} className="space-y-4">
      {erro && <Alert>{erro}</Alert>}

      <p className="text-sm text-slate-700">
        Você é o responsável designado por <strong>{store.companyName}</strong>. Ao aceitar, a sua
        empresa confirma o recebimento e o aceite dos requisitos contidos no Manual do Fornecedor
        nesta edição.
      </p>

      <dl className="grid gap-x-6 gap-y-1 rounded-lg bg-slate-50/60 p-3 text-sm sm:grid-cols-2">
        <Linha rotulo="Empresa" valor={store.companyName} />
        <Linha rotulo="Código" valor={store.protheusRef} />
        <Linha rotulo="Responsável" valor={user?.name ?? ''} />
        <Linha rotulo="E-mail" valor={user?.email ?? ''} />
      </dl>

      <Field
        label="Seu cargo na empresa"
        name="responsibleRole"
        className="max-w-sm"
        required
        value={cargo}
        onChange={(e) => setCargo(e.target.value)}
        error={campos.responsibleRole}
        hint="Consta do registro do aceite, como no formulário RQ 271-00."
      />

      <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={marcado}
          onChange={(e) => setMarcado(e.target.checked)}
          className="mt-0.5 size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        <span>
          Declaro que recebi e li o Manual do Fornecedor nesta edição, e aceito os requisitos nele
          contidos em nome de {store.companyName}.
        </span>
      </label>

      <div className="flex justify-end">
        <Button type="submit" disabled={!marcado || !cargo.trim()} loading={salvando}>
          Aceitar em nome da empresa
        </Button>
      </div>

      <p className="text-xs text-slate-500">
        O aceite fica registrado com o seu nome, cargo, data, hora, endereço IP e a impressão digital
        do arquivo aceito.
      </p>
    </form>
  );
}

/** Quem não é o responsável: o que está travando, e quem destrava. */
function AguardandoResponsavel({ store, edicao }: { store: StoreAccessDto; edicao: string }) {
  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-4">
      <p className="flex items-center gap-2 font-medium text-amber-900">
        <UserRoundX className="size-4" aria-hidden />
        O portal ainda não está liberado para a sua empresa.
      </p>

      {store.responsibleEmail ? (
        <>
          <p className="text-sm text-slate-700">
            O aceite do Manual é feito por <strong>{store.responsibleName}</strong> (
            <a
              href={`mailto:${store.responsibleEmail}`}
              className="text-brand-700 underline-offset-4 hover:underline"
            >
              {store.responsibleEmail}
            </a>
            ), designado responsável por <strong>{store.companyName}</strong>.
          </p>
          <p className="text-sm text-slate-700">
            Enquanto ele não aceitar a <strong>{edicao}</strong>, o portal fica indisponível para
            todos os usuários da empresa. Você já pode baixar e ler o Manual acima.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-700">
            Nenhum responsável foi designado para <strong>{store.companyName}</strong>, e só o
            responsável pode aceitar o Manual em nome da empresa.
          </p>
          <p className="text-sm text-slate-700">
            Peça à Qualidade da Weloze, em{' '}
            <a
              href="mailto:qualidade@weloze.com.br"
              className="text-brand-700 underline-offset-4 hover:underline"
            >
              qualidade@weloze.com.br
            </a>
            , que designe quem responde pela sua empresa no portal.
          </p>
        </>
      )}
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    // `min-w-0` no wrapper, e não só no `<dd>`: quem é item do grid é este
    // `<div>`, e item de grid nasce com `min-width: auto` — sem isto a razão
    // social alarga a coluna e leva a página inteira junto. Mesmo tropeço do
    // painel na fatia (j), no mesmo lugar.
    <div className="flex min-w-0 justify-between gap-4 py-0.5">
      <dt className="shrink-0 text-slate-500">{rotulo}</dt>
      <dd className="min-w-0 truncate text-right text-slate-800">{valor}</dd>
    </div>
  );
}
